import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/dealership/lib/api";
import PageHeader from "@/dealership/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { TrendingUp, TrendingDown, Wallet, Layers, Percent, Boxes, Receipt } from "lucide-react";

const CAT_COLORS = { Purchase: "#0F52BA", "Auction Fees": "#3B82F6", Transport: "#8B5CF6", Reconditioning: "#F59E0B", Other: "#A1A1AA", "HST Paid": "#DC2626" };

function Kpi({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <p className={`font-heading text-2xl font-extrabold mt-3 font-mono-data ${accent || ""}`}>{value}</p>
    </Card>
  );
}

export default function Financials() {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: ["financials"],
    queryFn: async () => (await api.get("/financials")).data,
  });

  if (isLoading || !data) {
    return (
      <div className="p-8">
        <PageHeader title="Profit & Costs" subtitle="Loading financials…" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      </div>
    );
  }

  const s = data.summary;

  return (
    <div className="p-8">
      <PageHeader
        title="Profit & Costs"
        subtitle="Where your money goes and what each unit earns."
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
        <Kpi icon={Wallet} label="Capital In Stock" value={money(s.total_invested_active)} testid="fin-invested" />
        <Kpi icon={TrendingUp} label="Total Revenue" value={money(s.total_revenue)} testid="fin-revenue" />
        <Kpi icon={Receipt} label="Gross Receipts (incl. HST)" value={money(s.gross_receipts)} testid="fin-gross-receipts" />
        <Kpi icon={s.total_profit >= 0 ? TrendingUp : TrendingDown} label="Realized Profit" value={money(s.total_profit)} accent={s.total_profit >= 0 ? "text-[#16A34A]" : "text-destructive"} testid="fin-profit" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <Kpi icon={TrendingUp} label="Gross Profit (incl. Net HST)" value={money(s.gross_profit_incl_tax)} accent={s.gross_profit_incl_tax >= 0 ? "text-[#16A34A]" : "text-destructive"} testid="fin-gross-profit-tax" />
        <Kpi icon={Percent} label="Avg Margin" value={`${s.avg_margin}%`} testid="fin-margin" />
        <Kpi icon={Layers} label="Total Cost (All Units)" value={money(s.total_cost_all)} testid="fin-cost-all" />
        <Kpi icon={Wallet} label="Cost of Units Sold" value={money(s.total_cost_sold)} testid="fin-cost-sold" />
        <Kpi icon={Boxes} label="Units Sold" value={s.units_sold} testid="fin-units-sold" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Kpi icon={TrendingUp} label="Avg Profit / Unit" value={money(s.avg_profit)} accent={s.avg_profit >= 0 ? "text-[#16A34A]" : "text-destructive"} testid="fin-avg-profit" />
      </div>

      <Card className="p-6 mb-8" data-testid="hst-position-card">
        <div className="flex items-center gap-2 mb-1">
          <Receipt className="h-5 w-5 text-primary" />
          <h3 className="font-heading text-lg font-bold">HST Position</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          HST you collected on sales vs. the input tax credits (ITCs) you paid acquiring units.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-border">
          <div className="px-1 sm:px-6 py-3 sm:py-0 first:pl-0">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">HST Collected on Sales</p>
            <p className="font-heading text-2xl font-extrabold mt-2 font-mono-data text-[#16A34A]" data-testid="hst-collected">{money(s.total_tax_collected)}</p>
          </div>
          <div className="px-1 sm:px-6 py-3 sm:py-0">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">HST Paid at Acquisition (ITC)</p>
            <p className="font-heading text-2xl font-extrabold mt-2 font-mono-data text-destructive" data-testid="hst-paid">− {money(s.hst_paid)}</p>
          </div>
          <div className="px-1 sm:px-6 py-3 sm:py-0">
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Net HST {s.net_hst >= 0 ? "Payable to CRA" : "Refund / Credit"}</p>
            <p className="font-heading text-2xl font-extrabold mt-2 font-mono-data" data-testid="hst-net">{money(Math.abs(s.net_hst))}</p>
          </div>
        </div>
      </Card>

      <Card className="p-6 mb-8">
        <h3 className="font-heading text-lg font-bold mb-4">Cost Breakdown by Category</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data.cost_breakdown} layout="vertical" margin={{ left: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
            <XAxis type="number" fontSize={12} stroke="#71717A" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
            <YAxis type="category" dataKey="category" fontSize={12} stroke="#71717A" width={100} />
            <Tooltip formatter={(v) => money(v)} />
            <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
              {data.cost_breakdown.map((c) => <Cell key={c.category} fill={CAT_COLORS[c.category] || "#0F52BA"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <Card className="overflow-hidden">
        <div className="px-6 pt-5 pb-2">
          <h3 className="font-heading text-lg font-bold">Per-Unit Profit & Loss</h3>
          <p className="text-sm text-muted-foreground">Realized profit on sold units (revenue − landed cost, incl. reconditioning). Click a unit for a full cost & revenue breakdown.</p>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Unit</TableHead>
              <TableHead>VIN</TableHead>
              <TableHead className="text-right">Recon</TableHead>
              <TableHead className="text-right">Total Cost</TableHead>
              <TableHead className="text-right">Revenue</TableHead>
              <TableHead className="text-right">HST Paid (ITC)</TableHead>
              <TableHead className="text-right">HST Collected</TableHead>
              <TableHead className="text-right">Profit</TableHead>
              <TableHead className="text-right">Margin</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.rows.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">No sold units yet. Profit appears once units are marked Sold or deals are finalized.</TableCell></TableRow>
            ) : (
              data.rows.map((r) => (
                <TableRow key={r.id} data-testid={`fin-row-${r.id}`} className="cursor-pointer"
                  onClick={() => r.deal_id === r.id ? navigate(`/dealership/bill/${r.deal_id}`) : navigate(`/dealership/financials/vehicle/${r.id}`)}>
                  <TableCell className="font-semibold">{r.label}<span className="block text-xs text-muted-foreground font-mono-data font-normal">{r.stock_number}</span></TableCell>
                  <TableCell className="font-mono-data text-xs">{r.vin || "—"}</TableCell>
                  <TableCell className="text-right font-mono-data text-muted-foreground" data-testid={`fin-recon-${r.id}`}>{money(r.recon)}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(r.cost)}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(r.revenue)}</TableCell>
                  <TableCell className="text-right font-mono-data text-muted-foreground">{money(r.hst_paid)}</TableCell>
                  <TableCell className="text-right font-mono-data text-muted-foreground">{money(r.tax_collected)}</TableCell>
                  <TableCell className={`text-right font-mono-data font-semibold ${r.profit >= 0 ? "text-[#16A34A]" : "text-destructive"}`}>{money(r.profit)}</TableCell>
                  <TableCell className="text-right">
                    <Badge className={`border-0 font-mono-data ${r.profit >= 0 ? "bg-green-500/12 text-green-400" : "bg-rose-500/12 text-rose-400"}`}>{r.margin}%</Badge>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
